/**
 * Places React Query hooks
 * Handles fetching and managing place data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '@/stores/useNotificationStore';
import * as placesApi from '@/services/placesApi';
import { del } from '@/services/api';
import { STALE_TIME } from '@/utils/constants';
import type { PlaceFilter } from '@/types/place';

/**
 * Query keys for places
 */
export const placeKeys = {
  all: ['places'] as const,
  lists: () => [...placeKeys.all, 'list'] as const,
  list: (filter: PlaceFilter) => [...placeKeys.lists(), filter] as const,
  details: () => [...placeKeys.all, 'detail'] as const,
  detail: (id: string) => [...placeKeys.details(), id] as const,
  stats: (jobId?: string) => [...placeKeys.all, 'stats', jobId] as const,
  jobResults: (jobId: string, filter?: PlaceFilter) => [...placeKeys.all, 'job', jobId, filter] as const,
};

/**
 * Hook to fetch paginated list of places
 * @param filter - Optional filter parameters
 * @param options - Additional options like refetchInterval
 */
export function usePlaces(
  filter?: PlaceFilter,
  options?: { refetchInterval?: number | false }
) {
  return useQuery({
    queryKey: placeKeys.list(filter || {}),
    queryFn: () => {
      // If jobId is provided, use job results endpoint
      if (filter?.jobId) {
        return placesApi.getJobResults(filter.jobId, filter);
      }
      return placesApi.getPlaces(filter);
    },
    staleTime: STALE_TIME.PLACES || 30000,
    placeholderData: (prev) => prev,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook to fetch a single place by ID
 */
export function usePlace(placeId: string | undefined) {
  return useQuery({
    queryKey: placeKeys.detail(placeId || ''),
    queryFn: () => placesApi.getPlace(placeId!),
    enabled: !!placeId,
    staleTime: STALE_TIME.PLACES || 30000,
  });
}

/**
 * Hook to fetch place with reviews
 */
export function usePlaceWithReviews(placeId: string | undefined) {
  return useQuery({
    queryKey: [...placeKeys.detail(placeId || ''), 'reviews'],
    queryFn: () => placesApi.getPlaceWithReviews(placeId!),
    enabled: !!placeId,
    staleTime: STALE_TIME.PLACES || 30000,
  });
}

/**
 * Hook to fetch place statistics
 */
export function usePlaceStats(jobId?: string) {
  return useQuery({
    queryKey: placeKeys.stats(jobId),
    queryFn: () => placesApi.getPlaceStats(jobId),
    staleTime: STALE_TIME.STATS || 60000,
  });
}

/**
 * Hook to fetch places for a specific job
 */
export function useJobPlaces(jobId: string, filter?: Omit<PlaceFilter, 'jobId'>) {
  return useQuery({
    queryKey: placeKeys.jobResults(jobId, filter),
    queryFn: () => placesApi.getJobResults(jobId, filter),
    enabled: !!jobId,
    staleTime: STALE_TIME.PLACES || 30000,
  });
}

/**
 * Hook to search places
 */
export function useSearchPlaces(query: string, options?: { limit?: number; jobId?: string }) {
  return useQuery({
    queryKey: [...placeKeys.lists(), 'search', query, options],
    queryFn: () => placesApi.searchPlaces(query, options),
    enabled: query.length >= 2,
    staleTime: 10000, // 10 seconds for search results
  });
}

/**
 * Hook to get categories (top 20 from stats, used for filter dropdowns)
 */
export function useCategories(jobId?: string) {
  return useQuery({
    queryKey: [...placeKeys.all, 'categories', jobId],
    queryFn: () => placesApi.getCategories(jobId),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to get ALL distinct categories (no limit, used for admin settings)
 */
export function useAllCategories() {
  return useQuery({
    queryKey: [...placeKeys.all, 'allCategories'],
    queryFn: () => placesApi.getAllCategories(),
    staleTime: 60000,
  });
}

/**
 * Hook to get search terms (original search queries used to find places)
 * Used for the search term filter dropdown
 * Optionally accepts excludeClickedByUserIds to compute remaining (unclicked) counts
 */
export function useSearchTerms(params?: {
  excludeClickedByUserIds?: string[];
  curatedCategoriesOnly?: boolean;
  nameBlacklistActive?: boolean;
}) {
  return useQuery({
    queryKey: [...placeKeys.all, 'searchTerms', params],
    queryFn: () => placesApi.getSearchTerms(params),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to get countries with place counts (respecting active filters except country).
 * Used for the country filter dropdown showing per-country counts.
 */
export function useCountries(filter?: Omit<PlaceFilter, 'country'>) {
  return useQuery({
    queryKey: [...placeKeys.all, 'countries', filter],
    queryFn: () => placesApi.getCountries(filter),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to delete a place
 */
export function useDeletePlace() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (placeId: string) => del<void>(`/places/${placeId}`),
    onSuccess: (_, placeId) => {
      queryClient.removeQueries({ queryKey: placeKeys.detail(placeId) });
      queryClient.invalidateQueries({ queryKey: placeKeys.lists() });
      success('Place deleted', 'The place has been removed');
    },
    onError: (err) => {
      error('Failed to delete place', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to delete multiple places
 */
export function useDeletePlaces() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: async (placeIds: string[]) => {
      await Promise.all(placeIds.map((id) => del<void>(`/places/${id}`)));
    },
    onSuccess: (_, placeIds) => {
      placeIds.forEach((id) => {
        queryClient.removeQueries({ queryKey: placeKeys.detail(id) });
      });
      queryClient.invalidateQueries({ queryKey: placeKeys.lists() });
      success('Places deleted', `${placeIds.length} places have been removed`);
    },
    onError: (err) => {
      error('Failed to delete places', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to get top rated places
 */
export function useTopRatedPlaces(jobId?: string, limit = 10) {
  return useQuery({
    queryKey: [...placeKeys.lists(), 'topRated', jobId, limit],
    queryFn: () => placesApi.getTopRatedPlaces(jobId, limit),
    staleTime: 60000,
  });
}

/**
 * Hook to get places with websites
 */
export function usePlacesWithWebsites(jobId?: string, limit = 50) {
  return useQuery({
    queryKey: [...placeKeys.lists(), 'withWebsites', jobId, limit],
    queryFn: () => placesApi.getPlacesWithWebsites(jobId, limit),
    staleTime: 60000,
  });
}

/**
 * Hook to get places with phone numbers
 */
export function usePlacesWithPhones(jobId?: string, limit = 50) {
  return useQuery({
    queryKey: [...placeKeys.lists(), 'withPhones', jobId, limit],
    queryFn: () => placesApi.getPlacesWithPhones(jobId, limit),
    staleTime: 60000,
  });
}

/**
 * Hook to rescan a place (refresh its details from Google Maps)
 */
export function usePlaceRescan() {
  const queryClient = useQueryClient();
  const { success, error, info } = useNotification();

  return useMutation({
    mutationFn: (placeId: string | number) => placesApi.rescanPlace(placeId),
    onMutate: (placeId) => {
      info('Rescanning...', `Fetching fresh data for place ${placeId}`);
    },
    onSuccess: (result, placeId) => {
      if (result.success && result.place) {
        // Invalidate and refetch related queries
        queryClient.invalidateQueries({ queryKey: placeKeys.detail(String(placeId)) });
        queryClient.invalidateQueries({ queryKey: placeKeys.lists() });

        const fieldsUpdated = result.extractedFields.length;
        success(
          'Rescan complete',
          `Updated ${fieldsUpdated} field${fieldsUpdated !== 1 ? 's' : ''} in ${(result.elapsedMs / 1000).toFixed(1)}s`
        );
      } else {
        error('Rescan failed', result.error || 'Could not extract data');
      }
    },
    onError: (err) => {
      error('Rescan failed', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

export default usePlaces;
